import React, {FC} from 'react';
import styled from 'styled-components';

import {buildContactsManagerAccountCreatePayload} from '../../../../libs/contacts-manager/src/lib/ui/web/helpers/trackingHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {makeContactsManagerAccountDetailsUrl} from '../../../helpers/contactsManagerHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {DomainValidityStatusesEnum} from '../../../models/api/domainModel';
import {CreateAccountOptions} from '../../../store/data/accounts/accountsActions';
import {CreateAccountFormState} from '../../../store/panels/accounts/createAccountForm/createAccountFormState';
import {
  ContactDetailsPanelCreateAccountSubState,
  ContactDetailsSubStateTypesEnum,
} from '../../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactsManagerCreateAccountFormState} from '../../../store/panels/contacts/contactsManager/sidePanel/contactsManagerCreateAccountFormState';
import {ContactsManagerSidePanelTypesEnum} from '../../../store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelStateBase';
import {
  buildEditAccountPayload,
  ContactDetailsEditActionsEnum,
  ContactDetailsTrackingDisplayContextOptionsEnum,
} from '../../contacts/contactDetailsTrackingHelpers';

/*
 * Types.
 */

interface CreateAccountFormProps {
  createAccountFormState: ContactsManagerCreateAccountFormState | ContactDetailsPanelCreateAccountSubState;
  isEditingAccountOnCard: boolean;
  createAccount: (
    createOptions: CreateAccountOptions,
    avatarFile: File | undefined,
  ) => Promise<AccountModel | undefined>;
  closeSidePanel: (newAccountId?: number) => void;
  navigateToLink: (arg: string) => void;

  onAccountCreated?: (arg: number) => void;
}

/*
 * Style.
 */

const StyledCreateAccountFooterDiv = styled.div`
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  display: flex;
  flex-direction: row-reverse;
  gap: 10px;
`;

/*
 * Component.
 */

export const CreateAccountFooter: FC<CreateAccountFormProps> = (props) => {
  const {
    createAccountFormState,
    isEditingAccountOnCard,
    createAccount,
    closeSidePanel,
    navigateToLink,
    onAccountCreated,
  } = props;
  const canCreate = computeCanCreate(createAccountFormState);
  const {name, description, domains, customAttributes, createStatus, avatarFile} = createAccountFormState;

  const onCreate = async () => {
    const newAccount = await createAccount(
      {
        name,
        description,
        domainNames: domains.map((domain) => domain.name),
        customAttributes: customAttributes.filter((attribute) => Boolean(attribute.value)),
      },
      avatarFile,
    );
    if (newAccount) {
      if (onAccountCreated) {
        onAccountCreated(newAccount.id);
      }

      // If we are in the contacts manager
      if (createAccountFormState.type === ContactsManagerSidePanelTypesEnum.CREATE_ACCOUNT) {
        trackInterfaceEvent(
          InterfaceEventsEnum.CONTACTS_MANAGER_ACCOUNT_CREATE,
          buildContactsManagerAccountCreatePayload(domains.length > 0),
        );
        navigateToLink(makeContactsManagerAccountDetailsUrl(newAccount.id));
      }
      // If we are in contact details, we can request closing the sidepanel as that will close the form
      if (createAccountFormState.type === ContactDetailsSubStateTypesEnum.CREATE_ACCOUNT) {
        trackInterfaceEvent(
          InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_ACCOUNT,
          buildEditAccountPayload(
            createAccountFormState.cardId,
            newAccount,
            ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
            isEditingAccountOnCard ? ContactDetailsEditActionsEnum.EDIT : ContactDetailsEditActionsEnum.ADD,
            true,
          ),
        );
        closeSidePanel(newAccount.id);
      }
    }
  };
  const isSaving = createStatus === AsyncStatusesEnum.LOADING;

  return (
    <StyledCreateAccountFooterDiv>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={!canCreate || isSaving}
        onClick={() => onCreate()}
      >
        {renderCreateButtonText(isSaving)}
      </Button>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={() => closeSidePanel()}>
        <Trans id="contacts-manager-accounts-create-form-cancel">Cancel</Trans>
      </Button>
    </StyledCreateAccountFooterDiv>
  );
};

function computeCanCreate(createAccountFormState: CreateAccountFormState) {
  const {name, domains} = createAccountFormState;

  // The name of the account is required
  if (!name) {
    return false;
  }

  // All the domains must be valid
  if (!domains.every((domain) => domain.validityStatus === DomainValidityStatusesEnum.VALID)) {
    return false;
  }

  return true;
}

function renderCreateButtonText(isSaving: boolean) {
  if (isSaving) {
    return <Trans id="contacts-manager-accounts-create-form-creating">Creating...</Trans>;
  }

  return <Trans id="contacts-manager-accounts-create-form-create">Create</Trans>;
}
